import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import {
  MessageSquare, Users, Phone, Mail, Search, RefreshCw, ExternalLink,
  Loader2, CheckCircle2, AlertCircle, ArrowRight, GripVertical,
  UserPlus, MessageCircle, Target, Trophy, XCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

// Lead stage configuration
const LEAD_STAGES = [
  { id: 'new_lead', label: 'New Lead', color: 'bg-blue-100 text-blue-800', icon: UserPlus },
  { id: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-800', icon: MessageCircle },
  { id: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-800', icon: Target },
  { id: 'closed_won', label: 'Closed Won', color: 'bg-green-100 text-green-800', icon: Trophy },
  { id: 'closed_lost', label: 'Closed Lost', color: 'bg-red-100 text-red-800', icon: XCircle }
];

const VendorMessages = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('inbox');
  const [loading, setLoading] = useState(true);
  const [chatwootConfig, setChatwootConfig] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stageCounts, setStageCounts] = useState({});
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [draggedLead, setDraggedLead] = useState(null);

  // Fetch Chatwoot configuration
  useEffect(() => {
    fetchChatwootConfig();
    fetchLeads();
  }, []);

  const fetchChatwootConfig = async () => {
    try {
      // First try to get config with SSO URL
      const data = await apiCall('/api/chatwoot/config');
      setChatwootConfig(data);
      
      if (!data.provisioned) {
        // Auto-provision
        await provisionChatwoot();
      } else if (data.provisioned && !data.sso_url && !data.platform_user_id) {
        // Vendor is provisioned but doesn't have SSO yet - try to upgrade
        try {
          const upgradeResult = await apiCall('/api/chatwoot/upgrade-sso', { method: 'POST' });
          if (upgradeResult.success && upgradeResult.sso_url) {
            setChatwootConfig(prev => ({ ...prev, sso_url: upgradeResult.sso_url }));
            toast({ title: 'SSO Enabled', description: 'Auto-login is now available!' });
          }
        } catch (upgradeError) {
        }
      }
    } catch (error) {
      console.error('Error fetching Chatwoot config:', error);
    } finally {
      setLoading(false);
    }
  };

  const provisionChatwoot = async () => {
    try {
      const result = await apiCall('/api/chatwoot/provision', { method: 'POST' });
      if (result.success) {
        toast({ title: 'Success', description: 'Messaging inbox created successfully!' });
        await fetchChatwootConfig();
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'Failed to set up messaging. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const fetchLeads = async () => {
    try {
      const data = await apiCall('/api/chatwoot/leads');
      setLeads(data.leads || []);
      setStageCounts(data.stage_counts || {});
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const updateLeadStage = async (leadId, newStage, conversationId) => {
    try {
      await apiCall(`/api/chatwoot/leads/${leadId}/stage`, {
        method: 'PUT',
        body: JSON.stringify({ 
          stage: newStage,
          conversation_id: conversationId
        })
      });
      
      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead.id === leadId ? { ...lead, lead_stage: newStage } : lead
        )
      );
      
      // Update counts
      fetchLeads();
      
      toast({ title: 'Lead Updated', description: `Moved to ${newStage.replace('_', ' ')}` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update lead', variant: 'destructive' });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, newStage) => {
    e.preventDefault();
    if (draggedLead && draggedLead.lead_stage !== newStage) {
      updateLeadStage(draggedLead.id, newStage, draggedLead.chatwoot_conversation_id);
    }
    setDraggedLead(null);
  };

  const getLeadsByStage = (stageId) => {
    return leads.filter(lead => lead.lead_stage === stageId);
  };

  const filteredLeads = leads.filter(lead => 
    !searchQuery || 
    lead.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.contact_phone?.includes(searchQuery) ||
    lead.contact_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render loading state
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[600px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-green-600" />
              Messages
            </h1>
            <p className="text-muted-foreground">
              Manage customer conversations and track leads
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchChatwootConfig(); fetchLeads(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {chatwootConfig?.sso_url && (
              <a href={chatwootConfig.sso_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Full Dashboard
                </Button>
              </a>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="inbox" className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Inbox
            </TabsTrigger>
            <TabsTrigger value="leads" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Lead Pipeline
              {leads.length > 0 && (
                <Badge variant="secondary" className="ml-1">{leads.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Inbox Tab - Embedded Chatwoot */}
          <TabsContent value="inbox" className="mt-4">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                {!chatwootConfig?.provisioned ? (
                  <div className="flex flex-col items-center justify-center h-[600px] text-center p-8">
                    <MessageSquare className="h-16 w-16 text-muted-foreground mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Set Up Your Inbox</h3>
                    <p className="text-muted-foreground mb-4 max-w-md">
                      Connect your messaging inbox to start receiving and responding to customer inquiries via WhatsApp.
                    </p>
                    <Button onClick={provisionChatwoot}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Set Up Messaging
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Info banner - different messaging based on SSO availability */}
                    <div className={`border-b px-4 py-3 flex items-center justify-between ${
                      chatwootConfig?.sso_url?.includes('sso_auth_token') || chatwootConfig?.sso_url?.includes('/login?')
                        ? 'bg-green-50 border-green-200'
                        : 'bg-amber-50 border-amber-200'
                    }`}>
                      <div className={`flex items-center gap-2 text-sm ${
                        chatwootConfig?.sso_url?.includes('sso_auth_token') || chatwootConfig?.sso_url?.includes('/login?')
                          ? 'text-green-800'
                          : 'text-amber-800'
                      }`}>
                        <AlertCircle className="h-4 w-4" />
                        <span>
                          {chatwootConfig?.sso_url?.includes('sso_auth_token') || chatwootConfig?.sso_url?.includes('/login?')
                            ? 'Click to open your inbox with auto-login'
                            : 'SSO not configured - You will need to log in to Chatwoot separately'}
                        </span>
                      </div>
                      <a href={chatwootConfig?.sso_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className={
                          chatwootConfig?.sso_url?.includes('sso_auth_token') || chatwootConfig?.sso_url?.includes('/login?')
                            ? 'border-green-300 text-green-700 hover:bg-green-100'
                            : 'border-amber-300 text-amber-700 hover:bg-amber-100'
                        }>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open Inbox
                        </Button>
                      </a>
                    </div>
                    {!iframeLoaded && (
                      <div className="absolute inset-0 top-[52px] flex items-center justify-center bg-muted/50 z-10">
                        <Loader2 className="h-8 w-8 animate-spin" />
                      </div>
                    )}
                    <iframe
                      src={chatwootConfig?.sso_url}
                      className="w-full h-[648px] border-0"
                      title="Chatwoot Messages"
                      onLoad={() => setIframeLoaded(true)}
                      onError={() => setIframeLoaded(true)}
                      allow="camera; microphone; clipboard-write"
                      data-testid="chatwoot-iframe"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lead Pipeline Tab - Kanban Board */}
          <TabsContent value="leads" className="mt-4">
            {/* Search */}
            <div className="mb-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search leads by name, phone, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-leads"
                />
              </div>
            </div>

            {/* Kanban Board */}
            <div className="grid grid-cols-5 gap-4 overflow-x-auto pb-4">
              {LEAD_STAGES.map(stage => {
                const StageIcon = stage.icon;
                const stageLeads = getLeadsByStage(stage.id).filter(lead =>
                  !searchQuery ||
                  lead.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  lead.contact_phone?.includes(searchQuery)
                );

                return (
                  <div
                    key={stage.id}
                    className="min-w-[280px]"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, stage.id)}
                  >
                    {/* Stage Header */}
                    <div className="flex items-center justify-between mb-3 px-2">
                      <div className="flex items-center gap-2">
                        <Badge className={stage.color}>
                          <StageIcon className="h-3 w-3 mr-1" />
                          {stage.label}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          ({stageCounts[stage.id] || 0})
                        </span>
                      </div>
                    </div>

                    {/* Lead Cards */}
                    <div className="space-y-2 min-h-[400px] bg-muted/30 rounded-lg p-2">
                      {stageLeads.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-32 text-muted-foreground text-sm">
                          <Users className="h-8 w-8 mb-2 opacity-50" />
                          No leads
                        </div>
                      ) : (
                        stageLeads.map(lead => (
                          <Card
                            key={lead.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, lead)}
                            className={`cursor-grab active:cursor-grabbing transition-shadow hover:shadow-md ${
                              draggedLead?.id === lead.id ? 'opacity-50' : ''
                            }`}
                            data-testid={`lead-card-${lead.id}`}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between mb-2">
                                <div className="font-medium text-sm truncate flex-1">
                                  {lead.contact_name || 'Unknown'}
                                </div>
                                <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              </div>
                              
                              {lead.contact_phone && (
                                <a 
                                  href={`tel:${lead.contact_phone}`}
                                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-1"
                                >
                                  <Phone className="h-3 w-3" />
                                  {lead.contact_phone}
                                </a>
                              )}
                              
                              {lead.contact_email && (
                                <a
                                  href={`mailto:${lead.contact_email}`}
                                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-1 truncate"
                                >
                                  <Mail className="h-3 w-3" />
                                  {lead.contact_email}
                                </a>
                              )}
                              
                              {lead.message_count > 0 && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                                  <MessageCircle className="h-3 w-3" />
                                  {lead.message_count} messages
                                </div>
                              )}
                              
                              <div className="text-xs text-muted-foreground mt-2">
                                {new Date(lead.created_at).toLocaleDateString()}
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Empty State */}
            {leads.length === 0 && (
              <Card className="mt-8">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <Users className="h-16 w-16 text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Leads Yet</h3>
                  <p className="text-muted-foreground max-w-md mb-4">
                    When customers message you via WhatsApp, they'll automatically appear here as leads.
                    Start a conversation to see your first lead!
                  </p>
                  <Button variant="outline" onClick={() => setActiveTab('inbox')}>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Go to Inbox
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {LEAD_STAGES.map(stage => {
            const StageIcon = stage.icon;
            return (
              <Card key={stage.id}>
                <CardContent className="pt-4 pb-3 text-center">
                  <StageIcon className={`h-5 w-5 mx-auto mb-1 ${stage.color.split(' ')[1]}`} />
                  <div className="text-2xl font-bold">{stageCounts[stage.id] || 0}</div>
                  <div className="text-xs text-muted-foreground">{stage.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VendorMessages;
